import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from 'store';
import { QUERY_KEYS } from 'service/querykeys';
import { notificationSSEClient } from 'service/notifications/sseClient';
import { applyPmoSSEEvent } from 'service/notifications/hooks';
import { applyHrmSSEEvent } from 'service/hrm-notifications/hooks';
import { SSENotificationEvent } from 'types/notifications';

// ─── Context ──────────────────────────────────────────────────────────────────

interface NotificationSSEContextValue {
  isConnected: boolean;
}

const NotificationSSEContext = createContext<NotificationSSEContextValue>({
  isConnected: false,
});

export const useNotificationSSE = () => useContext(NotificationSSEContext);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const NotificationSSEProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [isConnected, setIsConnected] = React.useState(false);

  // Keep accessToken ref so the SSE handlers always have the latest value
  // without re-subscribing on every token rotation
  const accessTokenRef = useRef(accessToken);
  useEffect(() => { accessTokenRef.current = accessToken; }, [accessToken]);

  // ── Route SSE notification events to the correct module handler ─────────────
  useEffect(() => {
    const unsubEvent = notificationSSEClient.onEvent((event: SSENotificationEvent) => {
      const token = accessTokenRef.current;
      if (event.module === 'PMO') {
        applyPmoSSEEvent(queryClient, token, event);
      } else if (event.module === 'HRM') {
        applyHrmSSEEvent(queryClient, token, event);
      }
    });

    return unsubEvent;
  }, [queryClient]);

  // ── Track connection state ──────────────────────────────────────────────────
  useEffect(() => {
    const unsubConnection = notificationSSEClient.onConnectionChange(setIsConnected);
    return unsubConnection;
  }, []);

  // ── On reconnect, re-fetch both module notification lists ──────────────────
  useEffect(() => {
    const unsubRecovery = notificationSSEClient.onRecovery(() => {
      const token = accessTokenRef.current;
      // PMO recovery
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.NOTIFICATIONS, token] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', token] });
      // HRM recovery
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS, token] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT, token] });
    });
    return unsubRecovery;
  }, [queryClient]);

  // ── Connect / disconnect based on auth state ────────────────────────────────
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      notificationSSEClient.connect();
    } else {
      notificationSSEClient.disconnect();
    }

    return () => {
      // Nothing to clean up here — disconnect is handled by auth changes
    };
  }, [isAuthenticated, accessToken]);

  return (
    <NotificationSSEContext.Provider value={{ isConnected }}>
      {children}
    </NotificationSSEContext.Provider>
  );
};
